import { LanguagesEnum } from '@/api/enums';
import { ListingRequest, ListingResponse } from '@/api/types';
import { Branch } from '@/client/branches';
import { File } from '@/client/files';
import { Group } from '@/client/groups';
import { MessageResponseModel } from '@/client/models';
import { ModifiedQuestion, QuestionType, Quiz } from '@/client/quizzes';
import { User } from '@/client/users';
import { TranslationFunctionType } from '@/common/types';
import {
  TargetEntities,
  TargetEntitiesRequest,
  TargetEntity,
} from '@/common/types/targets.type';
import { GenericForbiddenApiError } from '@/hooks/query';
import { UseMutationResult } from 'react-query';

// Send Survey Types
export type SecurityCultureSendSurveyFormValues = {
  typeFormId?: string;
  url?: string;
  target?: TargetEntity;
  targets?: TargetEntity[];
  branches?: Branch[];
  groups?: Group[];
  users?: User[];
  test?: SecurityCultureTestEmail;
  scheduledFor?: Date;
};

export type SecurityCultureSendSurvey = {
  branches?: string[];
  groups?: string[];
  users?: string[];
  typeFormId?: string;
  url?: string;
  test?: SecurityCultureTestEmail;
  emailId: string;
  scheduledFor?: Date;
};

export type SecurityCultureSendSurveyRequest = {
  accountId: string;
} & SecurityCultureSendSurvey;

export type SecurityCultureSendSurveyResponse = MessageResponseModel & {
  success: boolean;
  message: string;
  error: string;
};

export type SecurityCultureEmailMutation = UseMutationResult<
  SecurityCultureSendSurveyResponse,
  GenericForbiddenApiError,
  SecurityCultureSendSurveyRequest
>;

export type SecurityCultureTestEmail = {
  email?: string;
  firstName?: string;
  lastName?: string;
  language?: LanguagesEnum;
};

// Upload Survey Types
export type SecurityCultureUploadSurveyFormValues = {
  id?: string;
  file?: File;
  date?: Date;
  targets?: TargetEntity[];
  published?: boolean;
  isDemo?: boolean;
  scheduledFor?: Date | null;
};

export type SecurityCultureUploadSurveyRequest = {
  id?: string;
  accountId: string;
  file?: string;
  date?: Date;
  targets?: TargetEntitiesRequest;
  status?: SurveyStatus;
  scheduledFor?: Date | null;
  isDemo?: boolean;
};

export type SecurityCultureUploadSurveyMutation = UseMutationResult<
  SecurityCultureSurvey,
  GenericForbiddenApiError,
  SecurityCultureUploadSurveyRequest
>;

// Survey Types
export type SecurityCultureSurvey = {
  id?: string;
  file?: File;
  date?: Date;
  targets?: TargetEntities;
  status?: SurveyStatus;
  previousScore?: number;
  score?: number;
  benchmarkScore?: number;
  participated?: number;
  targetedUsersCount?: number;
  totalParticipants?: number;
  relatedSurveys: SecurityCultureSurvey[];
  isDemo?: boolean;
  scheduledFor?: Date | null;
  scheduleStatus?: SurveyScheduleStatusEnum;
  displayStatus?: SurveyDisplayStatusEnum | null;
  filteredUsersCount?: number;
  quiz: Quiz | null;
};

export type FilterableSurveyResult = {
  surveyScore: number;
  participantsCount: number;
  totalCount: number;
  benchmarkScore: number;
  previousScore: number;
  scoredByPeriod: FilterableSurveyScoredByPeriod[];
  categories: FilterableSurveyCategory[];
  feedback: SurveyFeedback[];
};

export type FilterableSurveyScoredByPeriod = {
  date: string;
  surveyScore: number;
};

export enum SurveyScheduleStatusEnum {
  WAITING = 'waiting',
  SCHEDULED = 'scheduled',
  SENT = 'sent',
}

export enum SurveyDisplayStatusEnum {
  SCHEDULED = 'scheduled',
  ACTIVE = 'active',
  COMPLETED = 'completed',
}

export type SecurityCultureSurveyTarget = {
  surveys: string[];
  targets: TargetEntities;
};

export type ListSurveysRequest = ListingRequest & {
  defaultCandidate?: boolean;
};
export type ListSurveysResponse = ListingResponse<SecurityCultureSurvey>;

export type FilterableSurveyRequest = {
  groups?: string[];
  branches?: string[];
  departments?: string[];
  managers?: string[];
  surveyId?: string[];
  language?: LanguagesEnum;
};

// Survey Category Types
export type SurveyCategory = {
  id: string;
  name: SurveyCategoryEnum;
  result: number;
  previousResult: number;
  benchmarkResult: number;
  isLow: boolean;
  filteredUsersCount?: number;
};

export type ListSurveyCategoriesRequest = ListingRequest & {
  accountId: string;
  surveyId: string;
};
export type ListSurveyCategoriesResponse = ListingResponse<SurveyCategory>;

export type FilterableSurveyCategory = {
  score: number;
  category: SurveyCategoryEnum;
  benchmarkScore: number;
  total: number;
  participants: number;
  previousScore?: number | null;
  questions: FilterableSurveyCategoryQuestion[];
};

export type FilterableSurveyCategoryQuestion = {
  benchmarkScore?: string;
  participants?: number;
  questionLabel?: string;
  score?: string;
  total?: number;
};

// Survey Category Question Types
export type SurveyQuestionEdit = {
  id: string;
  surveyCategory?: SurveyCategoryEnum;
  language?: string;
  description?: string;
  result?: number;
  previousResult?: number;
  benchmarkResult?: number;
  priority?: number;
  filteredUsersCount?: number;
};

export type ListSurveyCategoryQuestionsRequest = ListingRequest & {
  accountId: string;
  surveyId: string;
  categoryName: SurveyCategoryEnum;
};
export type ListSurveyCategoryQuestionsResponse =
  ListingResponse<SurveyQuestionEdit>;

// Survey Category Recommendation Types
export type SurveyRecommendation = {
  id: string;
  surveyCategory?: SurveyCategoryEnum;
  language?: string;
  title?: string;
  description?: string;
};

export type ListSurveyCategoryRecommendationsRequest = ListingRequest & {
  accountId: string;
  surveyId: string;
  categoryName: SurveyCategoryEnum;
};
export type ListSurveyCategoryRecommendationsResponse =
  ListingResponse<SurveyRecommendation>;

// Survey Feedback Types
export type SurveyFeedback = {
  id: string;
  survey?: string;
  description?: string;
  priority?: number;
};

export type ListSurveyFeedbackRequest = ListingRequest & {
  accountId: string;
  surveyId: string;
};
export type ListSurveyFeedbackResponse = ListingResponse<SurveyFeedback>;

// Delete Survey Types
export type DeleteSurveyRequest = {
  accountId: string;
  surveyId: string;
};

export type DeleteSurveyMutation = UseMutationResult<
  boolean,
  GenericForbiddenApiError,
  string
>;

// Enums
export enum SurveyStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
}

export enum SurveyCategoryEnum {
  MANAGEMENT = 'management',
  COLLEAGUES = 'colleagues',
  RESPONSIBILITY = 'responsibility',
  PSYCHOLOGICAL_SAFETY = 'psychologicalSafety',
  RESOURCES = 'resources',
  TIME = 'time',
  FEEDBACK = 'feedback',
  CUSTOM = 'custom',
}

export enum SecurityCultureDataSource {
  EXCEL_FILE = 'excel-file',
  TYPEFORM = 'typeform',
  QUIZ_ENGINE = 'quiz-engine',
}

export const questionIdOptions = (
  t: TranslationFunctionType,
  values: ModifiedQuestion[],
) => [
  {
    label: t(`sc.question.id.${SurveyCategoryEnum.MANAGEMENT}`) + '_1',
    value: SurveyCategoryEnum.MANAGEMENT + '_1',
    disabled: values.some(
      (value) => value.label === SurveyCategoryEnum.MANAGEMENT + '_1',
    ),
  },
  {
    label: t(`sc.question.id.${SurveyCategoryEnum.COLLEAGUES}`) + '_1',
    value: SurveyCategoryEnum.COLLEAGUES + '_1',
    disabled: values?.some(
      (value) => value.label === SurveyCategoryEnum.COLLEAGUES + '_1',
    ),
  },
  {
    label: t(`sc.question.id.${SurveyCategoryEnum.RESPONSIBILITY}`) + '_1',
    value: SurveyCategoryEnum.RESPONSIBILITY + '_1',
    disabled: values?.some(
      (value) => value.label === SurveyCategoryEnum.RESPONSIBILITY + '_1',
    ),
  },
  {
    label: t(`sc.question.id.${SurveyCategoryEnum.RESPONSIBILITY}`) + '_2',
    value: SurveyCategoryEnum.RESPONSIBILITY + '_2',
    disabled: values?.some(
      (value) => value.label === SurveyCategoryEnum.RESPONSIBILITY + '_2',
    ),
  },
  {
    label:
      t(`sc.question.id.${SurveyCategoryEnum.PSYCHOLOGICAL_SAFETY}`) + '_1',
    value: SurveyCategoryEnum.PSYCHOLOGICAL_SAFETY + '_1',
    disabled: values?.some(
      (value) => value.label === SurveyCategoryEnum.PSYCHOLOGICAL_SAFETY + '_1',
    ),
  },
  {
    label:
      t(`sc.question.id.${SurveyCategoryEnum.PSYCHOLOGICAL_SAFETY}`) + '_2',
    value: SurveyCategoryEnum.PSYCHOLOGICAL_SAFETY + '_2',
    disabled: values?.some(
      (value) => value.label === SurveyCategoryEnum.PSYCHOLOGICAL_SAFETY + '_2',
    ),
  },
  {
    label: t(`sc.question.id.${SurveyCategoryEnum.RESOURCES}`) + '_1',
    value: SurveyCategoryEnum.RESOURCES + '_1',
    disabled: values?.some(
      (value) => value.label === SurveyCategoryEnum.RESOURCES + '_1',
    ),
  },
  {
    label: t(`sc.question.id.${SurveyCategoryEnum.RESOURCES}`) + '_2',
    value: SurveyCategoryEnum.RESOURCES + '_2',
    disabled: values?.some(
      (value) => value.label === SurveyCategoryEnum.RESOURCES + '_2',
    ),
  },
  {
    label: t(`sc.question.id.${SurveyCategoryEnum.TIME}`) + '_1',
    value: SurveyCategoryEnum.TIME + '_1',
    disabled: values?.some(
      (value) => value.label === SurveyCategoryEnum.TIME + '_1',
    ),
  },
  {
    label: t('sc.question.id.feedback') + '_1',
    value: SurveyCategoryEnum.FEEDBACK + '_1',
    disabled: values?.some(
      (value) => value.label === SurveyCategoryEnum.FEEDBACK + '_1',
    ),
  },
  {
    label: t('sc.question.id.custom'),
    value: SurveyCategoryEnum.CUSTOM,
    disabled: false,
  },
];

export const questionType = (
  t: TranslationFunctionType,
  hasFeedback: boolean,
) => [
  {
    label: t('sc.question.type.feedback'),
    value: QuestionType.TEXT,
    disabled: hasFeedback,
  },
  {
    label: t('sc.question.type.scale'),
    value: QuestionType.RATING_SCALE,
  },
];

export enum SCViewEnum {
  ALL = 'all',
  FEEDBACK = 'feedback',
  CATEGORY = 'category',
}
